import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Dropdown,
  Menu,
  Table,
} from 'antd'
import { GlobalOutlined, DownOutlined, RightOutlined, ArrowRightOutlined } from '@ant-design/icons'
import colozooLogo from '../assets/colozoo_logo.svg'
import PageTitle from './PageTitle'
import PageLayout from './PageLayout'
import SiteFooter from './SiteFooter'
import { updateMetaTags } from '../utils/metaTagManager'
import { formatIngredientPercent } from '../utils/formatIngredientPercent'
import { displayCandlePenColorLabel } from '../utils/candlePenColorLabel'
import { candlePensConfig, watchSectionBackgroundStyle } from '../data/candlePens'
import {
  candlePensColorCompositions,
  candlePensProductInfo,
  type CandlePenIngredient,
} from '../data/candlePensIngredients'
import './CandlePens.css'
import './CandlePensIngredients.css'

const languageOptions = [
  { code: 'en-GB', label: 'English (GB)' },
  { code: 'en-US', label: 'English (US)' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'es', label: 'Español' },
]

const CandlePensIngredients = () => {
  const { t, i18n } = useTranslation('candlePensIngredients')
  const txt = (key: string) => (t(key) as string) || ''

  const language = i18n.resolvedLanguage || i18n.language || 'en-GB'
  const languageCode =
    language === 'en-US'
      ? 'EN'
      : language === 'en-GB'
        ? 'GB'
        : language.slice(0, 2).toUpperCase()
  const numberLocale = language.startsWith('en') ? language : language

  useEffect(() => {
    updateMetaTags({
      title: txt('meta.title'),
      description: txt('meta.description'),
      url: window.location.href,
    })
  }, [i18n.language, t])

  const columns = useMemo(
    () => [
      {
        title: txt('tableIngredient'),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: txt('tableCas'),
        dataIndex: 'cas',
        key: 'cas',
        width: 120,
      },
      {
        title: txt('tablePercent'),
        dataIndex: 'fraction',
        key: 'fraction',
        width: 100,
        align: 'right' as const,
        render: (fraction: number) => formatIngredientPercent(fraction, numberLocale),
      },
    ],
    [i18n.language, t, numberLocale],
  )

  const collapseItems = candlePensColorCompositions.map((variant) => ({
    key: variant.id,
    label: displayCandlePenColorLabel(variant.label),
    children: (
      <>
        <Table<CandlePenIngredient>
          className="czi-table"
          size="small"
          pagination={false}
          rowKey={(row) => `${row.cas}-${row.name}`}
          columns={columns}
          dataSource={variant.ingredients}
        />
      </>
    ),
  }))

  return (
    <>
      <PageTitle title={txt('meta.title')} />
      <PageLayout>
        <div className="czi-page">
          <div className="czi-panel">
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              overlay={
                <Menu>
                  {languageOptions.map((option) => (
                    <Menu.Item
                      key={option.code}
                      onClick={() => i18n.changeLanguage(option.code)}
                      style={{
                        backgroundColor:
                          i18n.resolvedLanguage === option.code ? '#279063' : 'transparent',
                        color: i18n.resolvedLanguage === option.code ? 'white' : 'inherit',
                        fontWeight: i18n.resolvedLanguage === option.code ? 'bold' : 'normal',
                      }}
                    >
                      {option.label}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <button type="button" className="cz-lang" aria-label="Language">
                <GlobalOutlined className="cz-lang-icon" />
                <span className="cz-lang-code">{languageCode}</span>
                <DownOutlined className="cz-lang-caret" />
              </button>
            </Dropdown>

            <img className="cz-logo" src={colozooLogo} alt="Colozoo" />

            <header className="czi-header">
              <h1>{t('pageTitle')}</h1>
              <p>{t('intro')}</p>
            </header>

            <Descriptions className="czi-meta" bordered size="small" column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label={t('ean')}>{candlePensProductInfo.ean}</Descriptions.Item>
              <Descriptions.Item label={t('volume')}>
                {t('volumeValue', { ml: candlePensProductInfo.volumeMlPerPen })}
              </Descriptions.Item>
              <Descriptions.Item label={t('setSize')}>
                {t('setSizeValue', { count: candlePensProductInfo.pensInSet })}
              </Descriptions.Item>
            </Descriptions>

            <h2 className="czi-section-title">{t('ingredientsHeading')}</h2>
            <p className="czi-section-hint">{t('ingredientsHint')}</p>

            <Collapse
              className="czi-collapse"
              items={collapseItems}
              expandIcon={({ isActive }) => (
                <RightOutlined rotate={isActive ? 90 : 0} style={{ fontSize: 11 }} />
              )}
            />

            <Alert
              className="czi-warning"
              type="warning"
              showIcon
              message={t('warningTitle')}
              description={t('warningBody')}
            />

            <p className="czi-inci-note">{t('inciNote')}</p>

            <section className="czi-related" aria-labelledby="czi-related-heading">
              <h2 id="czi-related-heading" className="czi-section-title">
                {t('relatedHeading')}
              </h2>
              <section className="cz-video czi-video-banner" style={watchSectionBackgroundStyle}>
                <div className="cz-video-inner">
                  <div className="cz-video-copy">
                    <div className="cz-video-body czi-video-body-tight">
                      <h2 className="czi-related-banner-title">{t('relatedCandlePens')}</h2>
                      <p>{t('relatedCandlePensDesc')}</p>
                      <Link to="/candle-pens" className="czi-related-banner-link">
                        <Button
                          type="primary"
                          size="large"
                          icon={<ArrowRightOutlined />}
                          className="cz-video-btn"
                        >
                          {t('relatedCandlePensButton')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <div className="cz-video-visual">
                    <Link
                      to="/candle-pens"
                      className="cz-video-thumb"
                      aria-label={txt('relatedCandlePensButton')}
                    >
                      <img
                        src={candlePensConfig.videoScreenshot}
                        alt={txt('relatedCandlePensThumbAlt')}
                      />
                    </Link>
                  </div>
                </div>
              </section>
              <div className="czi-contact">
                <strong>{t('contactHeading')}</strong>
                <br />
                {t('contactText')}{' '}
                <a href="mailto:support@colozoo.de">support@colozoo.de</a>{' '}
                {t('contactAfter')}
              </div>
            </section>
          </div>
          <SiteFooter />
        </div>
      </PageLayout>
    </>
  )
}

export default CandlePensIngredients
